/** API が返すエラーを表す例外 */
export class SpliApiError extends Error {
    status;
    code;
    details;
    constructor(status, code, message, details) {
        super(message);
        this.name = "SpliApiError";
        this.status = status;
        this.code = code;
        this.details = details;
    }
    /** 入力エラー (画面のフォームに表示すべきもの) か */
    get isValidationError() {
        return this.status === 400;
    }
    get isUnauthorized() {
        return this.status === 401;
    }
    get isNotFound() {
        return this.status === 404;
    }
}
export class SpliClient {
    baseUrl;
    options;
    constructor(options) {
        this.baseUrl = options.baseUrl.replace(/\/$/, "");
        this.options = options;
    }
    async call(method, path, body) {
        const doFetch = this.options.fetchImpl ?? globalThis.fetch;
        const token = (await this.options.getToken?.()) ?? null;
        let response;
        try {
            response = await doFetch(`${this.baseUrl}${path}`, {
                method,
                headers: {
                    // 本文が無いのに content-type を送ると、サーバー側で
                    // 「JSON なのに本文が空」として 400 になる (DELETE で発生していた)。
                    ...(body !== undefined ? { "content-type": "application/json" } : {}),
                    ...(token ? { authorization: `Bearer ${token}` } : {}),
                },
                ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
            });
        }
        catch (cause) {
            throw new SpliApiError(0, "NETWORK_ERROR", "サーバーに接続できませんでした", cause);
        }
        if (response.status === 204)
            return undefined;
        const text = await response.text();
        const payload = text ? safeJsonParse(text) : null;
        if (!response.ok) {
            if (response.status === 401)
                this.options.onUnauthorized?.();
            const error = payload
                ?.error;
            throw new SpliApiError(response.status, error?.code ?? "UNKNOWN", error?.message ?? `リクエストに失敗しました (${response.status})`, error?.details);
        }
        return payload;
    }
    // --- グループ -------------------------------------------------------------
    async createGroup(input) {
        const { group } = await this.call("POST", "/api/groups", input);
        return group;
    }
    async getGroup(shareToken) {
        const { group } = await this.call("GET", `/api/groups/${shareToken}`);
        return group;
    }
    async updateGroup(shareToken, input) {
        const { group } = await this.call("PATCH", `/api/groups/${shareToken}`, input);
        return group;
    }
    async linkGroup(shareToken) {
        const { group } = await this.call("POST", `/api/groups/${shareToken}/link`, {});
        return group;
    }
    // --- メンバー -------------------------------------------------------------
    async addMember(shareToken, input) {
        const { group } = await this.call("POST", `/api/groups/${shareToken}/members`, input);
        return group;
    }
    async updateMember(shareToken, memberId, input) {
        const { group } = await this.call("PATCH", `/api/groups/${shareToken}/members/${memberId}`, input);
        return group;
    }
    async removeMember(shareToken, memberId) {
        const { group } = await this.call("DELETE", `/api/groups/${shareToken}/members/${memberId}`);
        return group;
    }
    /** グループ内の「自分」を選んでアカウントと紐づける (ログイン必須) */
    async claimMember(shareToken, memberId) {
        const { group } = await this.call("POST", `/api/groups/${shareToken}/members/${memberId}/claim`, {});
        return group;
    }
    async releaseMember(shareToken, memberId) {
        const { group } = await this.call("DELETE", `/api/groups/${shareToken}/members/${memberId}/claim`);
        return group;
    }
    // --- 制約 -----------------------------------------------------------------
    async upsertConstraint(shareToken, input) {
        const { group } = await this.call("PUT", `/api/groups/${shareToken}/constraints`, input);
        return group;
    }
    async deleteConstraint(shareToken, constraintId) {
        const { group } = await this.call("DELETE", `/api/groups/${shareToken}/constraints/${constraintId}`);
        return group;
    }
    // --- 支出 -----------------------------------------------------------------
    async createExpense(shareToken, input) {
        const { group } = await this.call("POST", `/api/groups/${shareToken}/expenses`, input);
        return group;
    }
    async updateExpense(shareToken, expenseId, input) {
        const { group } = await this.call("PATCH", `/api/groups/${shareToken}/expenses/${expenseId}`, input);
        return group;
    }
    async deleteExpense(shareToken, expenseId) {
        const { group } = await this.call("DELETE", `/api/groups/${shareToken}/expenses/${expenseId}`);
        return group;
    }
    // --- 精算 -----------------------------------------------------------------
    async getSettlement(shareToken, ignoreConstraints) {
        const params = new URLSearchParams();
        if (ignoreConstraints)
            params.set("ignoreConstraints", "true");
        const query = params.size > 0 ? `?${params}` : "";
        return this.call("GET", `/api/groups/${shareToken}/settlement${query}`);
    }
    async recordSettlement(shareToken, input) {
        const { group } = await this.call("POST", `/api/groups/${shareToken}/settlements`, input);
        return group;
    }
    /** 受取人本人による「受け取りました」の確認 (ログイン + メンバー紐づけが必要) */
    async confirmSettlement(shareToken, paymentId) {
        const { group } = await this.call("POST", `/api/groups/${shareToken}/settlements/${paymentId}/confirm`, {});
        return group;
    }
    async deleteSettlement(shareToken, paymentId) {
        const { group } = await this.call("DELETE", `/api/groups/${shareToken}/settlements/${paymentId}`);
        return group;
    }
    // --- 入力候補 -------------------------------------------------------------
    async getSuggestions(shareToken, query = {}) {
        const params = new URLSearchParams();
        if (query.q)
            params.set("q", query.q);
        if (query.limit)
            params.set("limit", String(query.limit));
        const suffix = params.toString() ? `?${params.toString()}` : "";
        return this.call("GET", `/api/groups/${shareToken}/suggestions${suffix}`);
    }
    // --- アカウント -----------------------------------------------------------
    /** ① メールアドレスに確認コードを送る */
    requestLoginCode(input) {
        return this.call("POST", "/api/auth/code", input);
    }
    /** ② 確認コードで認証する。未登録ならここで登録される */
    verifyLoginCode(input) {
        return this.call("POST", "/api/auth/verify", input);
    }
    /** サーバーが有効にしている機能を取得する (認証不要) */
    async getConfig() {
        return this.call("GET", "/api/config");
    }
    async me() {
        const { user } = await this.call("GET", "/api/auth/me");
        return user;
    }
    async listMyGroups() {
        const { groups } = await this.call("GET", "/api/me/groups");
        return groups;
    }
    // --- 送金の受取先 ---------------------------------------------------------
    async getPayoutAccount() {
        const { payoutAccount } = await this.call("GET", "/api/me/payout-account");
        return payoutAccount;
    }
    async savePayoutAccount(input) {
        const { payoutAccount } = await this.call("PUT", "/api/me/payout-account", input);
        return payoutAccount;
    }
    async deletePayoutAccount() {
        await this.call("DELETE", "/api/me/payout-account");
    }
}
function safeJsonParse(text) {
    try {
        return JSON.parse(text);
    }
    catch {
        return null;
    }
}
//# sourceMappingURL=client.js.map